'use strict';

import { PIPELINE_STATUS_MOCK_SCENARIO, USE_PIPELINE_STATUS_API_MOCKS } from '@/config/env.config';
import {
	pipelineStatusFlowScenarioFromProjectPipeline,
	SINGLE_CORE_PROJECT_FLOW_SCENARIO,
	SINGLE_V7_PROJECT_FLOW_SCENARIO,
} from '../pipelineStatusFlowScenarios';
import { PipelineStatusWorkflowNames } from '../pipelineStatusTypes';
import { mockPipelineStatusGraphForPipeline } from './pipelineStatusGraphCatalog.mock';

export { SINGLE_CORE_PROJECT_FLOW_SCENARIO, SINGLE_V7_PROJECT_FLOW_SCENARIO };

/**
 * Literal API-shaped fixtures for `GET …/admin/pipeline-status/projects/{projectId}/facts`.
 *
 * - `SINGLE_V7_PROJECT_FLOW_MOCK_ROWS` — fact DTOs as returned by PortalApi (`PipelineStatusFactDto`).
 * - `SINGLE_V7_PROJECT_FLOW_ITERATION_MANIFESTS` — manifest DTOs (`PipelineStatusIterationManifestDto`).
 *
 * These arrays are not graph logic: they only mirror what the API would return. Tracked-node
 * identity lives on PortalApi `snapshot.graph` / `GET …/graphs`; Aperture adds presentation by node id.
 *
 * Use {@link getMockPipelineStatusSnapshotForScenario} for `{ facts, iterationManifests, graph }`.
 */

export const V7_PROJECT_ID = '3fa85f64-5717-4562-b3fc-2c963f66afa6';

export const V7_SAMPLE_SET_IDS = Object.freeze([
	'8b2d9c10-1111-4222-9333-444444444444',
	'8b2d9c10-1111-4222-9333-555555555555',
]);

/** @deprecated Use {@link V7_SAMPLE_SET_IDS} */
export const V7_SAMPLE_SET_ID = V7_SAMPLE_SET_IDS[0];

export const V7_CORRELATION_ID = 'b1000001-0001-4001-8001-000000000001';

/**
 * Literal fact rows for V7 / Project / Publishing (append-only; SS1 complete, SS2 in progress).
 * `iterationDimension` / `iterationValue` appear only where the API would return them on a fact row.
 */
const SINGLE_V7_PROJECT_FLOW_MOCK_ROWS = [
	{
		id: 'a0000001-0000-4000-8000-000000000001',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: 'V7',
		stage: 'V7ProjectPoller',
		status: 'Started',
		occurredAt: '2026-05-07T09:40:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'V7ProductService',
		serviceVersion: '2.3.4',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000002',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: 'V7',
		stage: 'V7ProjectPoller',
		status: 'Completed',
		occurredAt: '2026-05-07T09:50:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'V7ProductService',
		serviceVersion: '2.3.4',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000003',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: 'V7',
		stage: 'V7ProjectAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T09:59:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'PortalApi',
		serviceVersion: '2.3.4',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000004',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: 'V7',
		stage: 'V7ProjectAvailable',
		status: 'Completed',
		occurredAt: '2026-05-07T10:00:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'PortalApi',
		serviceVersion: '2.3.4',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000005',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: 'V7',
		stage: 'V7SampleSetAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T10:01:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'V7ProductService',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSet',
		iterationValue: '8b2d9c10-1111-4222-9333-444444444444',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000006',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: 'V7',
		stage: 'V7SampleSetAvailable',
		status: 'Completed',
		occurredAt: '2026-05-07T10:02:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'V7ProductService',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSet',
		iterationValue: '8b2d9c10-1111-4222-9333-444444444444',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000007',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: 'V7',
		stage: 'V7SampleSetAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T10:02:30.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'V7ProductService',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSet',
		iterationValue: '8b2d9c10-1111-4222-9333-555555555555',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000008',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: 'V7',
		stage: 'ProductSampleSetAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T10:03:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'PortalApi',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSet',
		iterationValue: '8b2d9c10-1111-4222-9333-444444444444',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000009',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: 'V7',
		stage: 'ProductSampleSetAvailable',
		status: 'Completed',
		occurredAt: '2026-05-07T10:04:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'PortalApi',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSet',
		iterationValue: '8b2d9c10-1111-4222-9333-444444444444',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000010',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: 'V7',
		stage: 'ProductSampleSetAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T10:04:30.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'PortalApi',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSet',
		iterationValue: '8b2d9c10-1111-4222-9333-555555555555',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000011',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: 'V7',
		stage: 'ProductSampleSetAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T10:05:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'BioVisService',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSet',
		iterationValue: '8b2d9c10-1111-4222-9333-444444444444',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000012',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: 'V7',
		stage: 'ProductSampleSetAvailable',
		status: 'Completed',
		occurredAt: '2026-05-07T10:06:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'BioVisService',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSet',
		iterationValue: '8b2d9c10-1111-4222-9333-444444444444',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000013',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: 'V7',
		stage: 'ProductSampleSetAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T10:06:15.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'BioVisService',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSet',
		iterationValue: '8b2d9c10-1111-4222-9333-555555555555',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000014',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: 'V7',
		stage: 'ProductSampleSetAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T10:06:30.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'PathwayExplorerApi',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSet',
		iterationValue: '8b2d9c10-1111-4222-9333-444444444444',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000015',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: 'V7',
		stage: 'ProductSampleSetAvailable',
		status: 'Completed',
		occurredAt: '2026-05-07T10:07:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'PathwayExplorerApi',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSet',
		iterationValue: '8b2d9c10-1111-4222-9333-444444444444',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000016',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: 'V7',
		stage: 'ProductSampleSetAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T10:07:15.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'PathwayExplorerApi',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSet',
		iterationValue: '8b2d9c10-1111-4222-9333-555555555555',
	},
];

/** One manifest for the V7ProjectPoller sample-set fan-out (not one manifest per graph node). */
const SINGLE_V7_PROJECT_FLOW_ITERATION_MANIFESTS = [
	{
		id: 'm0000001-0000-4000-8000-000000000001',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: 'V7',
		parentStage: 'V7ProjectPoller',
		parentService: 'V7ProductService',
		correlationId: V7_CORRELATION_ID,
		iterationDimension: 'SampleSet',
		iterationValues: [
			'8b2d9c10-1111-4222-9333-444444444444',
			'8b2d9c10-1111-4222-9333-555555555555',
		],
		createdAtUtc: '2026-05-07T09:40:30.000Z',
		sourceService: 'V7ProductService',
	},
];

const CORE_PROJECT_ID = '3fa85f64-5717-4562-b3fc-2c963f66afa7';
const CORE_CORRELATION_ID = 'b1000001-0001-4001-8001-000000000002';

const SINGLE_CORE_PROJECT_FLOW_MOCK_ROWS = [
	{
		id: 'a0000002-0000-4000-8000-000000000000',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: CORE_PROJECT_ID,
		pipeline: 'Core',
		stage: 'V7SampleSetAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T10:00:00.000Z',
		correlationId: CORE_CORRELATION_ID,
		service: 'V7ProductService',
		serviceVersion: '2.3.4',
	},
	{
		id: 'a0000002-0000-4000-8000-000000000001',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: CORE_PROJECT_ID,
		pipeline: 'Core',
		stage: 'V7SampleSetAvailable',
		status: 'Completed',
		occurredAt: '2026-05-07T10:00:00.000Z',
		correlationId: CORE_CORRELATION_ID,
		service: 'V7ProductService',
		serviceVersion: '2.3.4',
	},
];

// ---------------------------------------------------------------------------
// Scenario library for the customer tracker prototype (DTO-shaped rows only).
// Timestamps are relative to module load so in-progress scenarios render as
// current (not stale/Delayed) in the browser; `v7Delayed` is deliberately old.
// The legacy `singleV7ProjectFlow` fixture keeps its frozen timestamps because
// existing tests pin them.
// ---------------------------------------------------------------------------

const NOW_MS = Date.now();

/** @param {number} minutes */
function minutesAgoIso(minutes) {
	return new Date(NOW_MS - minutes * 60 * 1000).toISOString();
}

let mockFactIdCounter = 0;

/**
 * DTO-shaped V7 Publishing fact row builder (`PipelineStatusFactDto` fields only).
 * @param {{
 *   stage: string, status: string, service: string, minutesAgo: number,
 *   sampleSetId?: string, correlationId?: string, pipeline?: string, occurredAt?: string,
 *   service_omit?: boolean,
 * }} p
 */
function v7Fact(p) {
	mockFactIdCounter += 1;
	const row = {
		id: `f0000000-0000-4000-8000-${String(mockFactIdCounter).padStart(12, '0')}`,
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: p.pipeline ?? 'V7',
		stage: p.stage,
		status: p.status,
		occurredAt: p.occurredAt ?? minutesAgoIso(p.minutesAgo),
		correlationId: p.correlationId ?? V7_CORRELATION_ID,
		service: p.service,
		serviceVersion: '2.3.4',
		...(p.sampleSetId
			? { iterationDimension: 'SampleSet', iterationValue: p.sampleSetId }
			: {}),
	};
	if (p.service_omit) delete row.service;
	return row;
}

/** @param {string[]} sampleSetIds @param {number} minutesAgo */
function v7Manifest(sampleSetIds, minutesAgo) {
	return {
		id: `m0000000-0000-4000-8000-${String((mockFactIdCounter += 1)).padStart(12, '0')}`,
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: 'V7',
		parentStage: 'V7ProjectPoller',
		parentService: 'V7ProductService',
		correlationId: V7_CORRELATION_ID,
		iterationDimension: 'SampleSet',
		iterationValues: [...sampleSetIds],
		createdAtUtc: minutesAgoIso(minutesAgo),
		sourceService: 'V7ProductService',
	};
}

const [SS1, SS2] = V7_SAMPLE_SET_IDS;

/** Poller + project-available complete, all sample sets transferred + prepared. */
function buildV7CompletedThrough(minutesBase, sampleSetIds) {
	const rows = [
		v7Fact({ stage: 'V7ProjectPoller', status: 'Started', service: 'V7ProductService', minutesAgo: minutesBase + 30 }),
		v7Fact({ stage: 'V7ProjectPoller', status: 'Completed', service: 'V7ProductService', minutesAgo: minutesBase + 28 }),
		v7Fact({ stage: 'V7ProjectAvailable', status: 'Started', service: 'PortalApi', minutesAgo: minutesBase + 27 }),
		v7Fact({ stage: 'V7ProjectAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: minutesBase + 26 }),
	];
	sampleSetIds.forEach((ss, i) => {
		rows.push(
			v7Fact({ stage: 'V7SampleSetAvailable', status: 'Completed', service: 'V7ProductService', minutesAgo: minutesBase + 24 - i, sampleSetId: ss }),
			v7Fact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: minutesBase + 20 - i, sampleSetId: ss }),
			v7Fact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'BioVisService', minutesAgo: minutesBase + 18 - i, sampleSetId: ss }),
			v7Fact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PathwayExplorerApi', minutesAgo: minutesBase + 16 - i, sampleSetId: ss }),
		);
	});
	return rows;
}

const V7_EMPTY_ROWS = [];

const V7_RECEIVED_ROWS = [
	v7Fact({ stage: 'V7ProjectPoller', status: 'Started', service: 'V7ProductService', minutesAgo: 4 }),
];

const V7_TRANSFERRING_ROWS = [
	v7Fact({ stage: 'V7ProjectPoller', status: 'Started', service: 'V7ProductService', minutesAgo: 22 }),
	v7Fact({ stage: 'V7ProjectPoller', status: 'Completed', service: 'V7ProductService', minutesAgo: 20 }),
	v7Fact({ stage: 'V7ProjectAvailable', status: 'Started', service: 'PortalApi', minutesAgo: 18 }),
	v7Fact({ stage: 'V7SampleSetAvailable', status: 'Started', service: 'V7ProductService', minutesAgo: 16, sampleSetId: SS1 }),
	v7Fact({ stage: 'V7SampleSetAvailable', status: 'Completed', service: 'V7ProductService', minutesAgo: 12, sampleSetId: SS1 }),
];

/** Stage 3 active: transfers complete, downstream preparation partially complete. */
const V7_PREPARING_ROWS = [
	v7Fact({ stage: 'V7ProjectPoller', status: 'Started', service: 'V7ProductService', minutesAgo: 60 }),
	v7Fact({ stage: 'V7ProjectPoller', status: 'Completed', service: 'V7ProductService', minutesAgo: 58 }),
	v7Fact({ stage: 'V7ProjectAvailable', status: 'Started', service: 'PortalApi', minutesAgo: 57 }),
	v7Fact({ stage: 'V7ProjectAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 56 }),
	v7Fact({ stage: 'V7SampleSetAvailable', status: 'Completed', service: 'V7ProductService', minutesAgo: 54, sampleSetId: SS1 }),
	v7Fact({ stage: 'V7SampleSetAvailable', status: 'Completed', service: 'V7ProductService', minutesAgo: 53, sampleSetId: SS2 }),
	v7Fact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 40, sampleSetId: SS1 }),
	v7Fact({ stage: 'ProductSampleSetAvailable', status: 'Started', service: 'PortalApi', minutesAgo: 30, sampleSetId: SS2 }),
	v7Fact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'BioVisService', minutesAgo: 38, sampleSetId: SS1 }),
	v7Fact({ stage: 'ProductSampleSetAvailable', status: 'Started', service: 'BioVisService', minutesAgo: 28, sampleSetId: SS2 }),
	v7Fact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PathwayExplorerApi', minutesAgo: 36, sampleSetId: SS1 }),
	v7Fact({ stage: 'ProductSampleSetAvailable', status: 'Started', service: 'PathwayExplorerApi', minutesAgo: 26, sampleSetId: SS2 }),
];

const V7_READY_ROWS = buildV7CompletedThrough(90, [SS1, SS2]);

const V7_FAILED_ROWS = [
	...V7_PREPARING_ROWS,
	v7Fact({ stage: 'ProductSampleSetAvailable', status: 'Failed', service: 'PortalApi', minutesAgo: 10, sampleSetId: SS2 }),
];

/** In-progress rows whose newest fact is ~3 days old → honest Delayed derivation. */
const V7_DELAYED_ROWS = [
	v7Fact({ stage: 'V7ProjectPoller', status: 'Started', service: 'V7ProductService', minutesAgo: 3 * 24 * 60 + 40 }),
	v7Fact({ stage: 'V7ProjectPoller', status: 'Completed', service: 'V7ProductService', minutesAgo: 3 * 24 * 60 + 38 }),
	v7Fact({ stage: 'V7ProjectAvailable', status: 'Started', service: 'PortalApi', minutesAgo: 3 * 24 * 60 + 30 }),
];

/** Unknown enum value on a non-graph stage + an older unknown row (never latest) — tracker must be unaffected. */
const V7_UNKNOWN_STATUS_ROWS = [
	...V7_PREPARING_ROWS,
	v7Fact({ stage: 'SomeFutureStage', status: 'Queued', service: 'PortalApi', minutesAgo: 5 }),
	v7Fact({ stage: 'V7ProjectAvailable', status: 'Rescheduled', service: 'PortalApi', minutesAgo: 59 }),
];

/** Malformed rows (missing service / junk timestamp) — adapter fail-closes, tracker unaffected. */
const V7_MALFORMED_ROWS = [
	...V7_PREPARING_ROWS,
	v7Fact({ stage: 'V7ProjectAvailable', status: 'Failed', minutesAgo: 3, service: '', service_omit: true }),
	v7Fact({ stage: 'V7ProjectAvailable', status: 'Failed', service: 'PortalApi', minutesAgo: 0, occurredAt: 'not-a-timestamp' }),
];

const MANY_SAMPLE_SET_IDS = Object.freeze([
	'8b2d9c10-1111-4222-9333-666666666601',
	'8b2d9c10-1111-4222-9333-666666666602',
	'8b2d9c10-1111-4222-9333-666666666603',
	'8b2d9c10-1111-4222-9333-666666666604',
	'8b2d9c10-1111-4222-9333-666666666605',
	'8b2d9c10-1111-4222-9333-666666666606',
]);

const V7_MANY_SAMPLE_SETS_ROWS = [
	v7Fact({ stage: 'V7ProjectPoller', status: 'Started', service: 'V7ProductService', minutesAgo: 50 }),
	v7Fact({ stage: 'V7ProjectPoller', status: 'Completed', service: 'V7ProductService', minutesAgo: 48 }),
	v7Fact({ stage: 'V7ProjectAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 46 }),
	...MANY_SAMPLE_SET_IDS.slice(0, 4).map((ss, i) =>
		v7Fact({ stage: 'V7SampleSetAvailable', status: 'Completed', service: 'V7ProductService', minutesAgo: 40 - i, sampleSetId: ss })),
	v7Fact({ stage: 'V7SampleSetAvailable', status: 'Started', service: 'V7ProductService', minutesAgo: 30, sampleSetId: MANY_SAMPLE_SET_IDS[4] }),
];

// ---------------------------------------------------------------------------
// Core (sample-set grain) scenarios — tracker-boundary facts only:
// ProductService StatsCompleted + PortalApi PSSA ingestion.
// Core publications start on StatsCompleted; DataNormCompleted stamps Kits.
// DTO-shaped rows running through the real RTK mock adapter path.
// ---------------------------------------------------------------------------

export const CORE_SAMPLE_SET_IDS = Object.freeze([
	'7e2a4c19-8b3d-4f61-a9e0-1c5d8f2b7044',
	'b41f90d6-2c7a-48e3-9d15-6a0e3c8f9127',
	'0c8d5e33-f1a2-4b79-86c4-d3e7a91b2058',
]);
const [CSS1, CSS2, CSS3] = CORE_SAMPLE_SET_IDS;
const CORE_FLOW_CORRELATIONS = Object.freeze({
	[CSS1]: 'f6a1c0e8-2d44-4b91-9c37-8e15a0d4b6c2',
	[CSS2]: 'a19d4e70-5c18-4a2f-b8d3-7f0e6c1a9345',
	[CSS3]: 'c2e8b7a4-91d0-4e56-a3c1-5b8f2d047e19',
});
const CORE_OLD_CORRELATION = 'e4d9c1b0-3a72-4f85-9e16-2c8a5f0d7b33';

/**
 * DTO-shaped Core Publishing fact row (sample-set iteration identity, like PortalApi
 * stores SampleSetPipelineStatusMessage rows).
 * @param {{ stage: string, status: string, service: string, minutesAgo: number,
 *   sampleSetId: string, correlationId?: string }} p
 */
function coreFact(p) {
	return v7Fact({
		...p,
		pipeline: 'Core',
		correlationId: p.correlationId ?? CORE_FLOW_CORRELATIONS[p.sampleSetId],
	});
}

/** One ready, one publishing, one preparing — independent current correlations. */
const CORE_MULTI_SAMPLE_SET_ROWS = [
	coreFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 240, sampleSetId: CSS1 }),
	coreFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 230, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Started', service: 'PortalApi', minutesAgo: 225, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 220, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PathwayExplorerApi', minutesAgo: 218, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'HeatmapGenerationToolApi', minutesAgo: 216, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'BioVisService', minutesAgo: 214, sampleSetId: CSS1 }),
	coreFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 60, sampleSetId: CSS2 }),
	coreFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 50, sampleSetId: CSS2 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Started', service: 'PortalApi', minutesAgo: 45, sampleSetId: CSS2 }),
	coreFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 10, sampleSetId: CSS3 }),
];

/** Every current sample-set flow completed Portal, Pathway, Heatmap, and BioVis. */
const CORE_ALL_READY_ROWS = [
	coreFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 240, sampleSetId: CSS1 }),
	coreFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 230, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 220, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PathwayExplorerApi', minutesAgo: 218, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'HeatmapGenerationToolApi', minutesAgo: 216, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'BioVisService', minutesAgo: 214, sampleSetId: CSS1 }),
	coreFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 200, sampleSetId: CSS2 }),
	coreFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 190, sampleSetId: CSS2 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 180, sampleSetId: CSS2 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PathwayExplorerApi', minutesAgo: 178, sampleSetId: CSS2 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'HeatmapGenerationToolApi', minutesAgo: 176, sampleSetId: CSS2 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'BioVisService', minutesAgo: 174, sampleSetId: CSS2 }),
];

/**
 * Republication: the sample set was fully published under an older correlation and a
 * NEW deterministic correlation started. The old flow's Completed must not complete
 * the new flow; previous results remain accessible ("Updating" semantics).
 */
const CORE_REPUBLICATION_ROWS = [
	coreFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 20000, sampleSetId: CSS1, correlationId: CORE_OLD_CORRELATION }),
	coreFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 19990, sampleSetId: CSS1, correlationId: CORE_OLD_CORRELATION }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 19980, sampleSetId: CSS1, correlationId: CORE_OLD_CORRELATION }),
	coreFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 15, sampleSetId: CSS1 }),
];

/** One flow failed at the boundary; a second sample set is ready. */
const CORE_FAILED_ROWS = [
	coreFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 90, sampleSetId: CSS1 }),
	coreFact({ stage: 'StatsCompleted', status: 'Failed', service: 'ProductService', minutesAgo: 85, sampleSetId: CSS1 }),
	coreFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 240, sampleSetId: CSS2 }),
	coreFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 230, sampleSetId: CSS2 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 220, sampleSetId: CSS2 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PathwayExplorerApi', minutesAgo: 218, sampleSetId: CSS2 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'HeatmapGenerationToolApi', minutesAgo: 216, sampleSetId: CSS2 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'BioVisService', minutesAgo: 214, sampleSetId: CSS2 }),
];

/** Portal PSSA complete; Pathway still Started — backend keeps flow publishing (not Ready). */
const CORE_PATHWAY_INCOMPLETE_ROWS = [
	coreFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 120, sampleSetId: CSS1 }),
	coreFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 110, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 100, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Started', service: 'PathwayExplorerApi', minutesAgo: 95, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'HeatmapGenerationToolApi', minutesAgo: 94, sampleSetId: CSS1 }),
];

/** Portal PSSA complete; Heatmap still Started — backend keeps flow publishing. */
const CORE_HEATMAP_INCOMPLETE_ROWS = [
	coreFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 120, sampleSetId: CSS1 }),
	coreFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 110, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 100, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PathwayExplorerApi', minutesAgo: 95, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Started', service: 'HeatmapGenerationToolApi', minutesAgo: 94, sampleSetId: CSS1 }),
];

/** BioVis started but not complete — flow stays publishing until BioVis Completed. */
const CORE_BIOVIS_PARTICIPATING_ROWS = [
	coreFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 120, sampleSetId: CSS1 }),
	coreFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 110, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 100, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PathwayExplorerApi', minutesAgo: 95, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'HeatmapGenerationToolApi', minutesAgo: 94, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Started', service: 'BioVisService', minutesAgo: 90, sampleSetId: CSS1 }),
];

const CORE_EMPTY_ROWS = [];

/** Single sample-set flow still publishing after StatsCompleted. */
const CORE_DMP_ROWS = [
	coreFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 30, sampleSetId: CSS1 }),
	coreFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 20, sampleSetId: CSS1 }),
	coreFact({ stage: 'ProductSampleSetAvailable', status: 'Started', service: 'PortalApi', minutesAgo: 15, sampleSetId: CSS1 }),
];

/** In-progress flow with deliberately stale facts (renders Delayed). */
const CORE_DELAYED_ROWS = [
	coreFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 3 * 24 * 60, sampleSetId: CSS1 }),
];

/**
 * Kits publications: PortalApi ManifestAvailable forks to ProductService
 * ProjectAvailable → PortalApi ProjectAvailable, and to ProductService
 * DataNormCompleted → Portal / Pathway / BioVis.
 * @param {{ stage: string, status: string, service: string, minutesAgo: number, sampleSetId?: string, correlationId?: string }} p
 */
function kitsFact(p) {
	return v7Fact({
		...p,
		pipeline: 'Kits',
		correlationId: p.correlationId ?? (p.sampleSetId ? CORE_FLOW_CORRELATIONS[p.sampleSetId] : V7_PROJECT_ID),
	});
}

/** CSS1 DataNorm → Portal/Pathway/BioVis ready; CSS2 DataNorm → Portal publishing. */
const KITS_PUBLISHING_ROWS = [
	kitsFact({ stage: 'ManifestAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 400 }),
	kitsFact({ stage: 'ProjectAvailable', status: 'Completed', service: 'ProductService', minutesAgo: 399 }),
	kitsFact({ stage: 'ProjectAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 398 }),
	kitsFact({ stage: 'DataNormCompleted', status: 'Started', service: 'ProductService', minutesAgo: 240, sampleSetId: CSS1 }),
	kitsFact({ stage: 'DataNormCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 230, sampleSetId: CSS1 }),
	kitsFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 220, sampleSetId: CSS1 }),
	kitsFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PathwayExplorerApi', minutesAgo: 218, sampleSetId: CSS1 }),
	kitsFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'BioVisService', minutesAgo: 216, sampleSetId: CSS1 }),
	kitsFact({ stage: 'DataNormCompleted', status: 'Started', service: 'ProductService', minutesAgo: 30, sampleSetId: CSS2 }),
	kitsFact({ stage: 'DataNormCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 20, sampleSetId: CSS2 }),
	kitsFact({ stage: 'ProductSampleSetAvailable', status: 'Started', service: 'PortalApi', minutesAgo: 15, sampleSetId: CSS2 }),
	kitsFact({ stage: 'ProductSampleSetAvailable', status: 'Started', service: 'PathwayExplorerApi', minutesAgo: 14, sampleSetId: CSS2 }),
];

/** Manifest + catalog upsert only — no DataNorm yet (pizza Received). */
const KITS_RECEIVED_ROWS = [
	kitsFact({ stage: 'ManifestAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 40, sampleSetId: CSS1 }),
	kitsFact({ stage: 'ProjectAvailable', status: 'Completed', service: 'ProductService', minutesAgo: 39 }),
	kitsFact({ stage: 'ProjectAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 38 }),
];

/** Both sample sets Portal + Pathway + BioVis complete. */
const KITS_READY_ROWS = [
	kitsFact({ stage: 'ManifestAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 400 }),
	kitsFact({ stage: 'ProjectAvailable', status: 'Completed', service: 'ProductService', minutesAgo: 399 }),
	kitsFact({ stage: 'ProjectAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 398 }),
	kitsFact({ stage: 'DataNormCompleted', status: 'Started', service: 'ProductService', minutesAgo: 240, sampleSetId: CSS1 }),
	kitsFact({ stage: 'DataNormCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 230, sampleSetId: CSS1 }),
	kitsFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 220, sampleSetId: CSS1 }),
	kitsFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PathwayExplorerApi', minutesAgo: 218, sampleSetId: CSS1 }),
	kitsFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'BioVisService', minutesAgo: 216, sampleSetId: CSS1 }),
	kitsFact({ stage: 'DataNormCompleted', status: 'Started', service: 'ProductService', minutesAgo: 120, sampleSetId: CSS2 }),
	kitsFact({ stage: 'DataNormCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 110, sampleSetId: CSS2 }),
	kitsFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 100, sampleSetId: CSS2 }),
	kitsFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PathwayExplorerApi', minutesAgo: 98, sampleSetId: CSS2 }),
	kitsFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'BioVisService', minutesAgo: 96, sampleSetId: CSS2 }),
];

const KITS_FAILED_ROWS = [
	kitsFact({ stage: 'ManifestAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 400 }),
	kitsFact({ stage: 'ProjectAvailable', status: 'Completed', service: 'ProductService', minutesAgo: 399 }),
	kitsFact({ stage: 'ProjectAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 398 }),
	kitsFact({ stage: 'DataNormCompleted', status: 'Started', service: 'ProductService', minutesAgo: 40, sampleSetId: CSS1 }),
	kitsFact({ stage: 'DataNormCompleted', status: 'Failed', service: 'ProductService', minutesAgo: 30, sampleSetId: CSS1 }),
];

const KITS_EMPTY_ROWS = [];

// ---------------------------------------------------------------------------
// DiscoveryPanels (sample-set grain) scenarios — same boundary facts as Core
// but honestly tagged DiscoveryPanels.
// ---------------------------------------------------------------------------

export const DISCOVERY_SAMPLE_SET_IDS = Object.freeze([
	'd1e0c0a1-3333-4333-a444-777777777771',
	'd1e0c0a1-3333-4333-a444-777777777772',
	'd1e0c0a1-3333-4333-a444-777777777773',
]);
const [DSS1, DSS2, DSS3] = DISCOVERY_SAMPLE_SET_IDS;
const DISCOVERY_FLOW_CORRELATIONS = Object.freeze({
	[DSS1]: 'd1000001-0001-4001-8001-000000000001',
	[DSS2]: 'd1000001-0001-4001-8001-000000000002',
	[DSS3]: 'd1000001-0001-4001-8001-000000000003',
});

/**
 * @param {{ stage: string, status: string, service: string, minutesAgo: number,
 *   sampleSetId: string, correlationId?: string }} p
 */
function discoveryFact(p) {
	return v7Fact({
		...p,
		pipeline: 'DiscoveryPanels',
		correlationId: p.correlationId ?? DISCOVERY_FLOW_CORRELATIONS[p.sampleSetId],
	});
}

const DISCOVERY_MULTI_SAMPLE_SET_ROWS = [
	discoveryFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 240, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 230, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 220, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'DiscoveryPanelsApi', minutesAgo: 219, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'SmartPanelDataService', minutesAgo: 218, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSmartPanelVisualizationDataAvailable', status: 'Completed', service: 'DiscoveryPanelsApi', minutesAgo: 217, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 60, sampleSetId: DSS2 }),
	discoveryFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 50, sampleSetId: DSS2 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Started', service: 'PortalApi', minutesAgo: 45, sampleSetId: DSS2 }),
	discoveryFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 10, sampleSetId: DSS3 }),
];

const DISCOVERY_ALL_READY_ROWS = [
	discoveryFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 240, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 230, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 220, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'DiscoveryPanelsApi', minutesAgo: 219, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'SmartPanelDataService', minutesAgo: 218, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSmartPanelVisualizationDataAvailable', status: 'Completed', service: 'DiscoveryPanelsApi', minutesAgo: 217, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 200, sampleSetId: DSS2 }),
	discoveryFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 190, sampleSetId: DSS2 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 180, sampleSetId: DSS2 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'DiscoveryPanelsApi', minutesAgo: 179, sampleSetId: DSS2 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'SmartPanelDataService', minutesAgo: 178, sampleSetId: DSS2 }),
	discoveryFact({ stage: 'ProductSmartPanelVisualizationDataAvailable', status: 'Completed', service: 'DiscoveryPanelsApi', minutesAgo: 177, sampleSetId: DSS2 }),
];

/** Portal + DiscoveryPanelsApi complete; SmartPanelDataService still running. */
const DISCOVERY_SMART_PANEL_INCOMPLETE_ROWS = [
	discoveryFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 120, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 110, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 100, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'DiscoveryPanelsApi', minutesAgo: 98, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Started', service: 'SmartPanelDataService', minutesAgo: 95, sampleSetId: DSS1 }),
];

const DISCOVERY_FAILED_ROWS = [
	discoveryFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 90, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'StatsCompleted', status: 'Failed', service: 'ProductService', minutesAgo: 85, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 240, sampleSetId: DSS2 }),
	discoveryFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 230, sampleSetId: DSS2 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 220, sampleSetId: DSS2 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'DiscoveryPanelsApi', minutesAgo: 219, sampleSetId: DSS2 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'SmartPanelDataService', minutesAgo: 218, sampleSetId: DSS2 }),
	discoveryFact({ stage: 'ProductSmartPanelVisualizationDataAvailable', status: 'Completed', service: 'DiscoveryPanelsApi', minutesAgo: 217, sampleSetId: DSS2 }),
];

const DISCOVERY_EMPTY_ROWS = [];

/** Discovery publishing in flight (ProductService → Portal / DP API / SmartPanel). */
const DISCOVERY_ORDINARY_PROCESSING_ROWS = [
	discoveryFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 60, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 50, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 40, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Started', service: 'DiscoveryPanelsApi', minutesAgo: 35, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Started', service: 'SmartPanelDataService', minutesAgo: 34, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 20, sampleSetId: DSS2 }),
];

const DISCOVERY_ORDINARY_READY_ROWS = [
	discoveryFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 120, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 110, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 100, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'DiscoveryPanelsApi', minutesAgo: 95, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'SmartPanelDataService', minutesAgo: 94, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSmartPanelVisualizationDataAvailable', status: 'Completed', service: 'DiscoveryPanelsApi', minutesAgo: 93, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 90, sampleSetId: DSS2 }),
	discoveryFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 80, sampleSetId: DSS2 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 70, sampleSetId: DSS2 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'DiscoveryPanelsApi', minutesAgo: 65, sampleSetId: DSS2 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'SmartPanelDataService', minutesAgo: 64, sampleSetId: DSS2 }),
	discoveryFact({ stage: 'ProductSmartPanelVisualizationDataAvailable', status: 'Completed', service: 'DiscoveryPanelsApi', minutesAgo: 63, sampleSetId: DSS2 }),
];

/** Publishing nodes complete; visualization Started, so the sample set stays in progress. */
const DISCOVERY_SMART_PANEL_VIZ_INCOMPLETE_ROWS = [
	discoveryFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 120, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 110, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 100, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'DiscoveryPanelsApi', minutesAgo: 98, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'SmartPanelDataService', minutesAgo: 95, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSmartPanelVisualizationDataAvailable', status: 'Started', service: 'DiscoveryPanelsApi', minutesAgo: 90, sampleSetId: DSS1 }),
];

const DISCOVERY_SMART_PANEL_READY_ROWS = [
	discoveryFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 120, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 110, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 100, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'DiscoveryPanelsApi', minutesAgo: 98, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'SmartPanelDataService', minutesAgo: 95, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSmartPanelVisualizationDataAvailable', status: 'Completed', service: 'DiscoveryPanelsApi', minutesAgo: 90, sampleSetId: DSS1 }),
];

const DISCOVERY_SMART_PANEL_FAILED_ROWS = [
	discoveryFact({ stage: 'StatsCompleted', status: 'Started', service: 'ProductService', minutesAgo: 120, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'StatsCompleted', status: 'Completed', service: 'ProductService', minutesAgo: 110, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 100, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Completed', service: 'DiscoveryPanelsApi', minutesAgo: 98, sampleSetId: DSS1 }),
	discoveryFact({ stage: 'ProductSampleSetAvailable', status: 'Failed', service: 'SmartPanelDataService', minutesAgo: 95, sampleSetId: DSS1 }),
];

export const MOCK_PROJECT_CODE = 'PROJ-1008';

const MOCK_SAMPLE_SET_NAMES = Object.freeze({
	[SS1]: 'Plasma',
	[SS2]: 'Serum',
	[CSS1]: 'Plasma',
	[CSS2]: 'Serum',
	[CSS3]: 'Urine',
	[DSS1]: 'Panel A',
	[DSS2]: 'Panel B',
	[DSS3]: 'Panel C',
	...Object.fromEntries(MANY_SAMPLE_SET_IDS.map((id, i) => [id, `Set ${i + 1}`])),
});

/** @param {string | undefined} sampleSetId */
function mockSampleSetName(sampleSetId) {
	return sampleSetId ? MOCK_SAMPLE_SET_NAMES[sampleSetId] : undefined;
}

/** @param {Record<string, unknown>} row */
function withMockDisplayNames(row) {
	const details = { projectCode: MOCK_PROJECT_CODE };
	const sampleSetName = mockSampleSetName(row.iterationValue);
	if (sampleSetName) details.sampleSetName = sampleSetName;
	return { ...row, detailsJson: JSON.stringify(details) };
}

/** @param {string} sampleSetId @param {string} state @param {object} [over] */
function coreFlowDto(sampleSetId, state, over = {}) {
	return {
		sampleSetId,
		correlationId: CORE_FLOW_CORRELATIONS[sampleSetId],
		state,
		startedAt: minutesAgoIso(240),
		readyAt: state === 'ready' ? minutesAgoIso(220) : undefined,
		failedAt: state === 'failed' ? minutesAgoIso(220) : undefined,
		isRepublication: false,
		sampleSetName: mockSampleSetName(sampleSetId),
		...over,
	};
}

/** @param {string} sampleSetId @param {string} state @param {object} [over] */
function discoveryFlowDto(sampleSetId, state, over = {}) {
	return {
		sampleSetId,
		correlationId: DISCOVERY_FLOW_CORRELATIONS[sampleSetId],
		state,
		startedAt: minutesAgoIso(240),
		readyAt: state === 'ready' ? minutesAgoIso(220) : undefined,
		failedAt: state === 'failed' ? minutesAgoIso(220) : undefined,
		isRepublication: false,
		sampleSetName: mockSampleSetName(sampleSetId),
		...over,
	};
}

/** @param {string} sampleSetId @param {string} state @param {object} [over] */
function kitsFlowDto(sampleSetId, state, over = {}) {
	return {
		sampleSetId,
		correlationId: CORE_FLOW_CORRELATIONS[sampleSetId],
		state,
		startedAt: minutesAgoIso(240),
		readyAt: state === 'ready' ? minutesAgoIso(220) : undefined,
		failedAt: state === 'failed' ? minutesAgoIso(220) : undefined,
		isRepublication: false,
		sampleSetName: mockSampleSetName(sampleSetId),
		...over,
	};
}

/** @param {Array<{ state: string }>} flows */
function summarizeFlows(flows) {
	const ready = flows.filter((f) => f.state === 'ready').length;
	const failed = flows.filter((f) => f.state === 'failed').length;
	const inProgress = flows.length - ready - failed;
	return { ready, inProgress, failed, allReady: flows.length > 0 && ready === flows.length };
}

/**
 * Builds PortalApi-shaped `currentFlows` for mock scenarios that exercise
 * backend-authoritative Ready / V7 run selection.
 * @param {string} scenarioKey
 * @returns {Record<string, unknown> | null}
 */
function publicationFromSampleSetFlow(flow, pipeline) {
	return {
		pipeline,
		subjectId: flow.sampleSetId,
		correlationId: flow.correlationId,
		startedAt: flow.startedAt,
		supersedesEarlier: Boolean(flow.isRepublication),
	};
}

function publicationsFromLegacy(legacy) {
	const publications = [];
	if (legacy.v7Run) {
		publications.push({
			pipeline: 'V7',
			subjectId: V7_PROJECT_ID,
			correlationId: legacy.v7Run.correlationId,
			startedAt: legacy.v7Run.startedAt,
			supersedesEarlier: Boolean(legacy.v7Run.supersedesEarlierRun),
		});
	}
	for (const flow of legacy.coreSampleSetFlows ?? []) publications.push(publicationFromSampleSetFlow(flow, 'Core'));
	for (const flow of legacy.discoveryPanelsSampleSetFlows ?? []) publications.push(publicationFromSampleSetFlow(flow, 'DiscoveryPanels'));
	for (const flow of legacy.kitsSampleSetFlows ?? []) publications.push(publicationFromSampleSetFlow(flow, 'Kits'));
	return publications;
}

export function buildMockCurrentFlowsForScenario(scenarioKey) {
	const key = String(scenarioKey ?? '').trim();
	if (key === 'mergedReceived' || key === 'mergedReady' || key === 'mergedFailed') {
		return {
			publications: [{
				pipeline: 'Merged',
				subjectId: V7_PROJECT_ID,
				correlationId: MERGED_CORRELATION_ID,
				startedAt: minutesAgoIso(61),
				supersedesEarlier: false,
			}],
		};
	}
	if (key === 'mergedNoFacts') return { publications: [] };
	const legacy = buildLegacyCurrentFlows(key);
	if (legacy == null) return null;
	return { publications: publicationsFromLegacy(legacy) };
}

function buildLegacyCurrentFlows(scenarioKey) {
	const key = String(scenarioKey ?? '').trim();
	switch (key) {
		case 'coreMultiSampleSet':
			return {
				coreSampleSetFlows: [
					coreFlowDto(CSS1, 'ready'),
					coreFlowDto(CSS2, 'publishing'),
					coreFlowDto(CSS3, 'preparing'),
				],
				coreSummary: { ready: 1, inProgress: 2, failed: 0, allReady: false },
			};
		case 'coreAllReady':
			return {
				coreSampleSetFlows: [
					coreFlowDto(CSS1, 'ready'),
					coreFlowDto(CSS2, 'ready'),
				],
				coreSummary: { ready: 2, inProgress: 0, failed: 0, allReady: true },
			};
		case 'coreRepublication':
			return {
				coreSampleSetFlows: [coreFlowDto(CSS1, 'preparing', { isRepublication: true })],
				coreSummary: { ready: 0, inProgress: 1, failed: 0, allReady: false },
			};
		case 'coreFailed':
			return {
				coreSampleSetFlows: [
					coreFlowDto(CSS1, 'failed'),
					coreFlowDto(CSS2, 'ready'),
				],
				coreSummary: { ready: 1, inProgress: 0, failed: 1, allReady: false },
			};
		case 'coreDmp':
			return {
				coreSampleSetFlows: [coreFlowDto(CSS1, 'publishing')],
				coreSummary: { ready: 0, inProgress: 1, failed: 0, allReady: false },
			};
		case 'coreDelayed':
			return {
				coreSampleSetFlows: [coreFlowDto(CSS1, 'preparing', { startedAt: minutesAgoIso(3 * 24 * 60) })],
				coreSummary: { ready: 0, inProgress: 1, failed: 0, allReady: false },
			};
		case 'coreEmpty':
			return {
				coreSampleSetFlows: [],
				coreSummary: null,
			};
		case 'corePathwayIncomplete':
		case 'coreHeatmapIncomplete':
		case 'coreBioVisParticipating':
			return {
				coreSampleSetFlows: [coreFlowDto(CSS1, 'publishing')],
				coreSummary: { ready: 0, inProgress: 1, failed: 0, allReady: false },
			};
		case 'discoveryPanelsMultiSampleSet':
			return {
				discoveryPanelsSampleSetFlows: [
					discoveryFlowDto(DSS1, 'ready'),
					discoveryFlowDto(DSS2, 'publishing'),
					discoveryFlowDto(DSS3, 'preparing'),
				],
				discoveryPanelsSummary: { ready: 1, inProgress: 2, failed: 0, allReady: false },
			};
		case 'discoveryPanelsOrdinaryProcessing':
			return {
				discoveryPanelsSampleSetFlows: [
					discoveryFlowDto(DSS1, 'publishing'),
					discoveryFlowDto(DSS2, 'preparing'),
				],
				discoveryPanelsSummary: { ready: 0, inProgress: 2, failed: 0, allReady: false },
			};
		case 'discoveryPanelsOrdinaryReady':
			return {
				discoveryPanelsSampleSetFlows: [
					discoveryFlowDto(DSS1, 'ready'),
					discoveryFlowDto(DSS2, 'ready'),
				],
				discoveryPanelsSummary: { ready: 2, inProgress: 0, failed: 0, allReady: true },
			};
		case 'discoveryPanelsAllReady':
			return {
				discoveryPanelsSampleSetFlows: [
					discoveryFlowDto(DSS1, 'ready'),
					discoveryFlowDto(DSS2, 'ready'),
				],
				discoveryPanelsSummary: { ready: 2, inProgress: 0, failed: 0, allReady: true },
			};
		case 'discoveryPanelsSmartPanelIncomplete':
		case 'discoveryPanelsSmartPanelMicrobiome':
		case 'discoveryPanelsSmartPanelBiologicalStress':
		case 'discoveryPanelsSmartPanelLiverFibrosis':
		case 'discoveryPanelsSmartPanelKidneyFunction':
			return {
				discoveryPanelsSampleSetFlows: [discoveryFlowDto(DSS1, 'publishing')],
				discoveryPanelsSummary: { ready: 0, inProgress: 1, failed: 0, allReady: false },
			};
		case 'discoveryPanelsSmartPanelVizIncomplete':
		case 'discoveryPanelsSmartPanelReady':
			return {
				discoveryPanelsSampleSetFlows: [discoveryFlowDto(DSS1, 'ready')],
				discoveryPanelsSummary: { ready: 1, inProgress: 0, failed: 0, allReady: true },
			};
		case 'discoveryPanelsSmartPanelFailed':
			return {
				discoveryPanelsSampleSetFlows: [discoveryFlowDto(DSS1, 'failed')],
				discoveryPanelsSummary: { ready: 0, inProgress: 0, failed: 1, allReady: false },
			};
		case 'discoveryPanelsFailed':
			return {
				discoveryPanelsSampleSetFlows: [
					discoveryFlowDto(DSS1, 'failed'),
					discoveryFlowDto(DSS2, 'ready'),
				],
				discoveryPanelsSummary: { ready: 1, inProgress: 0, failed: 1, allReady: false },
			};
		case 'discoveryPanelsEmpty':
			return {
				discoveryPanelsSampleSetFlows: [],
				discoveryPanelsSummary: null,
			};
		case 'kitsPublishing':
			return {
				kitsSampleSetFlows: [
					kitsFlowDto(CSS1, 'ready'),
					kitsFlowDto(CSS2, 'publishing'),
				],
				kitsSummary: { ready: 1, inProgress: 1, failed: 0, allReady: false },
			};
		case 'kitsReceived':
			return {
				kitsSampleSetFlows: [kitsFlowDto(CSS1, 'received')],
				kitsSummary: { ready: 0, inProgress: 1, failed: 0, allReady: false },
			};
		case 'kitsAllReady':
			return {
				kitsSampleSetFlows: [
					kitsFlowDto(CSS1, 'ready'),
					kitsFlowDto(CSS2, 'ready'),
				],
				kitsSummary: { ready: 2, inProgress: 0, failed: 0, allReady: true },
			};
		case 'kitsFailed':
			return {
				kitsSampleSetFlows: [kitsFlowDto(CSS1, 'failed')],
				kitsSummary: { ready: 0, inProgress: 0, failed: 1, allReady: false },
			};
		case 'kitsEmpty':
			return {
				kitsSampleSetFlows: [],
				kitsSummary: null,
			};
		case 'v7Ready':
			// Align with buildV7CompletedThrough(90): poller Started = minutesBase + 30.
			return {
				v7Run: {
					correlationId: V7_CORRELATION_ID,
					startedAt: minutesAgoIso(120),
					ready: true,
					failed: false,
					expectedSampleSetIds: [...V7_SAMPLE_SET_IDS],
					completedSampleSetIds: [...V7_SAMPLE_SET_IDS],
					supersedesEarlierRun: false,
				},
			};
		case 'v7Failed':
			// Align with buildV7CompletedThrough(30) + Failed overlay (~minutesBase + 30).
			return {
				v7Run: {
					correlationId: V7_CORRELATION_ID,
					startedAt: minutesAgoIso(60),
					ready: false,
					failed: true,
					expectedSampleSetIds: [...V7_SAMPLE_SET_IDS],
					completedSampleSetIds: [V7_SAMPLE_SET_IDS[0]],
					supersedesEarlierRun: false,
				},
			};
		case 'v7Delayed':
			return {
				v7Run: {
					correlationId: V7_CORRELATION_ID,
					startedAt: minutesAgoIso(3 * 24 * 60 + 40),
					ready: false,
					failed: false,
					expectedSampleSetIds: [...V7_SAMPLE_SET_IDS],
					completedSampleSetIds: [],
					supersedesEarlierRun: false,
				},
			};
		case 'v7ManySampleSets':
			return {
				v7Run: {
					correlationId: V7_CORRELATION_ID,
					startedAt: minutesAgoIso(50),
					ready: false,
					failed: false,
					expectedSampleSetIds: [...MANY_SAMPLE_SET_IDS],
					completedSampleSetIds: [],
					supersedesEarlierRun: false,
				},
			};
		case 'v7UnknownStatus':
		case 'v7Malformed':
		case SINGLE_V7_PROJECT_FLOW_SCENARIO:
		case 'v7Preparing':
			// Align with V7_PREPARING_ROWS earliest Started (60m).
			return {
				v7Run: {
					correlationId: V7_CORRELATION_ID,
					startedAt: minutesAgoIso(60),
					ready: false,
					failed: false,
					expectedSampleSetIds: [...V7_SAMPLE_SET_IDS],
					completedSampleSetIds: [],
					supersedesEarlierRun: false,
				},
			};
		case 'v7Transferring':
			return {
				v7Run: {
					correlationId: V7_CORRELATION_ID,
					startedAt: minutesAgoIso(22),
					ready: false,
					failed: false,
					expectedSampleSetIds: [...V7_SAMPLE_SET_IDS],
					completedSampleSetIds: [V7_SAMPLE_SET_IDS[0]],
					supersedesEarlierRun: false,
				},
			};
		case 'v7Received':
			return {
				v7Run: {
					correlationId: V7_CORRELATION_ID,
					startedAt: minutesAgoIso(4),
					ready: false,
					failed: false,
					expectedSampleSetIds: [...V7_SAMPLE_SET_IDS],
					completedSampleSetIds: [],
					supersedesEarlierRun: false,
				},
			};
		case 'v7Empty':
			return {
				v7Run: null,
			};
		default:
			return null;
	}
}

const MERGED_CORRELATION_ID = 'd1000001-0001-4001-8001-000000000001';

/** Merged shell created; PortalProjectEvent not yet consumed. */
const MERGED_RECEIVED_ROWS = [
	v7Fact({ stage: 'MergeProjects', status: 'Started', service: 'PortalApi', minutesAgo: 61, pipeline: 'Merged', correlationId: MERGED_CORRELATION_ID }),
	v7Fact({ stage: 'MergeProjects', status: 'Completed', service: 'PortalApi', minutesAgo: 60, pipeline: 'Merged', correlationId: MERGED_CORRELATION_ID }),
];

/** Merged shell created and PortalApi consumed PortalProjectEvent. */
const MERGED_READY_ROWS = [
	v7Fact({ stage: 'MergeProjects', status: 'Started', service: 'PortalApi', minutesAgo: 61, pipeline: 'Merged', correlationId: MERGED_CORRELATION_ID }),
	v7Fact({ stage: 'MergeProjects', status: 'Completed', service: 'PortalApi', minutesAgo: 60, pipeline: 'Merged', correlationId: MERGED_CORRELATION_ID }),
	v7Fact({ stage: 'ProjectAvailable', status: 'Completed', service: 'PortalApi', minutesAgo: 59, pipeline: 'Merged', correlationId: MERGED_CORRELATION_ID }),
];

const MERGED_FAILED_ROWS = [
	v7Fact({ stage: 'MergeProjects', status: 'Started', service: 'PortalApi', minutesAgo: 61, pipeline: 'Merged', correlationId: MERGED_CORRELATION_ID }),
	v7Fact({ stage: 'MergeProjects', status: 'Failed', service: 'PortalApi', minutesAgo: 60, pipeline: 'Merged', correlationId: MERGED_CORRELATION_ID }),
];

/** Merged project with no portal facts yet (e.g. shell call failed upstream). */
const MERGED_NO_FACTS_ROWS = [];

/**
 * Registry of mock scenarios. `flowScenario` selects the graph definition; `kind`
 * selects the customer presentation ('v7' | 'core' | 'unsupported');
 * `supported=false` marks pipelines the customer tracker must render as unavailable.
 */
const MOCK_SCENARIO_REGISTRY = Object.freeze({
	[SINGLE_V7_PROJECT_FLOW_SCENARIO]: {
		flowScenario: SINGLE_V7_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'v7',
		facts: SINGLE_V7_PROJECT_FLOW_MOCK_ROWS,
		iterationManifests: SINGLE_V7_PROJECT_FLOW_ITERATION_MANIFESTS,
	},
	[SINGLE_CORE_PROJECT_FLOW_SCENARIO]: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'core',
		facts: SINGLE_CORE_PROJECT_FLOW_MOCK_ROWS,
		iterationManifests: [],
	},
	v7Empty: { flowScenario: SINGLE_V7_PROJECT_FLOW_SCENARIO, supported: true, kind: 'v7', facts: V7_EMPTY_ROWS, iterationManifests: [] },
	v7Received: { flowScenario: SINGLE_V7_PROJECT_FLOW_SCENARIO, supported: true, kind: 'v7', facts: V7_RECEIVED_ROWS, iterationManifests: [] },
	v7Transferring: {
		flowScenario: SINGLE_V7_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'v7',
		facts: V7_TRANSFERRING_ROWS,
		iterationManifests: [v7Manifest([SS1, SS2], 21)],
	},
	v7Preparing: {
		flowScenario: SINGLE_V7_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'v7',
		facts: V7_PREPARING_ROWS,
		iterationManifests: [v7Manifest([SS1, SS2], 59)],
	},
	v7Ready: {
		flowScenario: SINGLE_V7_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'v7',
		facts: V7_READY_ROWS,
		iterationManifests: [v7Manifest([SS1, SS2], 119)],
	},
	v7Failed: {
		flowScenario: SINGLE_V7_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'v7',
		facts: V7_FAILED_ROWS,
		iterationManifests: [v7Manifest([SS1, SS2], 59)],
	},
	v7Delayed: {
		flowScenario: SINGLE_V7_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'v7',
		facts: V7_DELAYED_ROWS,
		iterationManifests: [v7Manifest([SS1, SS2], 3 * 24 * 60 + 39)],
	},
	v7UnknownStatus: {
		flowScenario: SINGLE_V7_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'v7',
		facts: V7_UNKNOWN_STATUS_ROWS,
		iterationManifests: [v7Manifest([SS1, SS2], 59)],
	},
	v7Malformed: {
		flowScenario: SINGLE_V7_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'v7',
		facts: V7_MALFORMED_ROWS,
		iterationManifests: [v7Manifest([SS1, SS2], 59)],
	},
	v7ManySampleSets: {
		flowScenario: SINGLE_V7_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'v7',
		facts: V7_MANY_SAMPLE_SETS_ROWS,
		iterationManifests: [v7Manifest([...MANY_SAMPLE_SET_IDS], 49)],
	},
	coreUnsupported: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: false,
		kind: 'unsupported',
		facts: SINGLE_CORE_PROJECT_FLOW_MOCK_ROWS,
		iterationManifests: [],
	},
	coreMultiSampleSet: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'core',
		facts: CORE_MULTI_SAMPLE_SET_ROWS,
		iterationManifests: [],
	},
	coreAllReady: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'core',
		facts: CORE_ALL_READY_ROWS,
		iterationManifests: [],
	},
	coreRepublication: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'core',
		facts: CORE_REPUBLICATION_ROWS,
		iterationManifests: [],
	},
	coreFailed: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'core',
		facts: CORE_FAILED_ROWS,
		iterationManifests: [],
	},
	coreDmp: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'core',
		facts: CORE_DMP_ROWS,
		iterationManifests: [],
	},
	coreDelayed: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'core',
		facts: CORE_DELAYED_ROWS,
		iterationManifests: [],
	},
	coreEmpty: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'core',
		facts: CORE_EMPTY_ROWS,
		iterationManifests: [],
	},
	corePathwayIncomplete: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'core',
		facts: CORE_PATHWAY_INCOMPLETE_ROWS,
		iterationManifests: [],
	},
	coreHeatmapIncomplete: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'core',
		facts: CORE_HEATMAP_INCOMPLETE_ROWS,
		iterationManifests: [],
	},
	coreBioVisParticipating: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'core',
		facts: CORE_BIOVIS_PARTICIPATING_ROWS,
		iterationManifests: [],
	},
	kitsPublishing: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'kits',
		facts: KITS_PUBLISHING_ROWS,
		iterationManifests: [],
	},
	kitsReceived: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'kits',
		facts: KITS_RECEIVED_ROWS,
		iterationManifests: [],
	},
	kitsAllReady: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'kits',
		facts: KITS_READY_ROWS,
		iterationManifests: [],
	},
	kitsFailed: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'kits',
		facts: KITS_FAILED_ROWS,
		iterationManifests: [],
	},
	kitsEmpty: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'kits',
		facts: KITS_EMPTY_ROWS,
		iterationManifests: [],
	},
	discoveryPanelsMultiSampleSet: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'discoveryPanels',
		facts: DISCOVERY_MULTI_SAMPLE_SET_ROWS,
		iterationManifests: [],
	},
	discoveryPanelsOrdinaryProcessing: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'discoveryPanels',
		facts: DISCOVERY_ORDINARY_PROCESSING_ROWS,
		iterationManifests: [],
	},
	discoveryPanelsOrdinaryReady: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'discoveryPanels',
		facts: DISCOVERY_ORDINARY_READY_ROWS,
		iterationManifests: [],
	},
	discoveryPanelsAllReady: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'discoveryPanels',
		facts: DISCOVERY_ALL_READY_ROWS,
		iterationManifests: [],
	},
	discoveryPanelsSmartPanelIncomplete: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'discoveryPanels',
		facts: DISCOVERY_SMART_PANEL_INCOMPLETE_ROWS,
		iterationManifests: [],
	},
	discoveryPanelsSmartPanelVizIncomplete: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'discoveryPanels',
		facts: DISCOVERY_SMART_PANEL_VIZ_INCOMPLETE_ROWS,
		iterationManifests: [],
	},
	discoveryPanelsSmartPanelReady: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'discoveryPanels',
		facts: DISCOVERY_SMART_PANEL_READY_ROWS,
		iterationManifests: [],
	},
	discoveryPanelsSmartPanelFailed: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'discoveryPanels',
		facts: DISCOVERY_SMART_PANEL_FAILED_ROWS,
		iterationManifests: [],
	},
	discoveryPanelsSmartPanelMicrobiome: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'discoveryPanels',
		facts: DISCOVERY_SMART_PANEL_INCOMPLETE_ROWS,
		iterationManifests: [],
	},
	discoveryPanelsSmartPanelBiologicalStress: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'discoveryPanels',
		facts: DISCOVERY_SMART_PANEL_INCOMPLETE_ROWS,
		iterationManifests: [],
	},
	discoveryPanelsSmartPanelLiverFibrosis: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'discoveryPanels',
		facts: DISCOVERY_SMART_PANEL_VIZ_INCOMPLETE_ROWS,
		iterationManifests: [],
	},
	discoveryPanelsSmartPanelKidneyFunction: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'discoveryPanels',
		facts: DISCOVERY_SMART_PANEL_READY_ROWS,
		iterationManifests: [],
	},
	discoveryPanelsFailed: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'discoveryPanels',
		facts: DISCOVERY_FAILED_ROWS,
		iterationManifests: [],
	},
	discoveryPanelsEmpty: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'discoveryPanels',
		facts: DISCOVERY_EMPTY_ROWS,
		iterationManifests: [],
	},
	mergedReceived: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'merged',
		facts: MERGED_RECEIVED_ROWS,
		iterationManifests: [],
	},
	mergedReady: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'merged',
		facts: MERGED_READY_ROWS,
		iterationManifests: [],
	},
	mergedFailed: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'merged',
		facts: MERGED_FAILED_ROWS,
		iterationManifests: [],
	},
	mergedNoFacts: {
		flowScenario: SINGLE_CORE_PROJECT_FLOW_SCENARIO,
		supported: true,
		kind: 'merged',
		facts: MERGED_NO_FACTS_ROWS,
		iterationManifests: [],
	},
});

const MOCK_FACT_ROWS_BY_SCENARIO = Object.fromEntries(
	Object.entries(MOCK_SCENARIO_REGISTRY).map(([k, v]) => [k, v.facts]),
);

const MOCK_ITERATION_MANIFESTS_BY_SCENARIO = Object.fromEntries(
	Object.entries(MOCK_SCENARIO_REGISTRY).map(([k, v]) => [k, v.iterationManifests]),
);

/** All selectable mock scenario keys (superset of flow scenario keys). */
export const PIPELINE_STATUS_MOCK_SCENARIO_KEYS = new Set(Object.keys(MOCK_SCENARIO_REGISTRY));

/**
 * @param {unknown} key
 * @returns {string}
 */
export function requirePipelineStatusMockScenarioKey(key) {
	const k = String(key ?? '').trim();
	if (!PIPELINE_STATUS_MOCK_SCENARIO_KEYS.has(k)) {
		throw new Error(
			`[pipelineStatus] Unknown pipeline status mock scenario ${JSON.stringify(key)}. Expected one of: ${[...PIPELINE_STATUS_MOCK_SCENARIO_KEYS].join(', ')}`,
		);
	}
	return k;
}

/**
 * Graph flow scenario backing a mock scenario (mock keys are a superset of flow keys).
 * @param {unknown} mockScenarioKey
 * @returns {string}
 */
export function flowScenarioForMockScenario(mockScenarioKey) {
	const k = requirePipelineStatusMockScenarioKey(mockScenarioKey);
	return MOCK_SCENARIO_REGISTRY[k].flowScenario;
}

/**
 * Whether the customer tracker supports the pipeline behind a mock scenario
 * (`coreUnsupported` / the Core flow must not render the V7 tracker).
 * @param {unknown} mockScenarioKey
 */
export function isPipelineStatusMockScenarioSupported(mockScenarioKey) {
	const k = requirePipelineStatusMockScenarioKey(mockScenarioKey);
	return MOCK_SCENARIO_REGISTRY[k].supported;
}

/**
 * Customer presentation for a mock scenario: 'v7' (project run tracker),
 * 'core' / 'discoveryPanels' / 'kits' (sample-set publication tracker), 'merged'
 * (Received → Ready), or 'unsupported' (honest unavailable).
 * @param {unknown} mockScenarioKey
 * @returns {'v7' | 'core' | 'discoveryPanels' | 'kits' | 'merged' | 'unsupported'}
 */
export function pipelineProgressKindForMockScenario(mockScenarioKey) {
	const k = requirePipelineStatusMockScenarioKey(mockScenarioKey);
	return MOCK_SCENARIO_REGISTRY[k].kind ?? 'unsupported';
}

/**
 * Dev-only runtime scenario override for the mock preview page: `?pipelineStatusScenario=<key>`.
 * Read at call time (mock mode only); returns undefined outside a browser or when unset.
 */
export function readPipelineStatusMockScenarioQueryOverride() {
	if (!USE_PIPELINE_STATUS_API_MOCKS) return undefined;
	if (typeof window === 'undefined' || !window.location) return undefined;
	try {
		const raw = new URLSearchParams(window.location.search).get('pipelineStatusScenario');
		const k = raw != null ? String(raw).trim() : '';
		return k !== '' ? k : undefined;
	} catch {
		return undefined;
	}
}

/**
 * Shallow-clones snapshot rows so tests can mutate without affecting fixtures.
 * @param {Record<string, unknown>[]} rows
 * @returns {Record<string, unknown>[]}
 */
function cloneRows(rows) {
	return rows.map((r) => {
		const row = { ...r };
		if (Array.isArray(row.iterationValues)) {
			row.iterationValues = [...row.iterationValues];
		}
		return row;
	});
}

/**
 * Resolves which mock fixture scenario to serve when pipeline status API mocks are enabled (`USE_PIPELINE_STATUS_API_MOCKS` in `env.config.js`).
 * Precedence: `?pipelineStatusScenario=` query override (dev preview) → `PIPELINE_STATUS_MOCK_SCENARIO` env →
 * `projectPipeline` like live mode ({@link pipelineStatusFlowScenarioFromProjectPipeline}; default V7 flow).
 * Any explicit key must be a registered mock scenario (superset of flow keys).
 *
 * @param {unknown} [projectPipeline] Portal `ProjectDescription.pipeline` when no explicit override is set.
 * @returns {string} mock scenario key (use {@link flowScenarioForMockScenario} for the graph flow)
 */
export function requirePipelineStatusMockScenarioFromEnv(projectPipeline) {
	if (!USE_PIPELINE_STATUS_API_MOCKS) {
		throw new Error(
			'[pipelineStatus] requirePipelineStatusMockScenarioFromEnv is only valid when pipeline status API mocks are enabled',
		);
	}
	const queryOverride = readPipelineStatusMockScenarioQueryOverride();
	if (queryOverride != null) {
		return requirePipelineStatusMockScenarioKey(queryOverride);
	}
	const override = PIPELINE_STATUS_MOCK_SCENARIO;
	if (override != null && String(override).trim() !== '') {
		return requirePipelineStatusMockScenarioKey(override);
	}
	// Mirror live routing honestly: V7 → v7 flow, Core → core flow, Kits → the kits
	// sample-set scenario, Merged → the merged shell scenario, and any other EXPLICIT
	// value (corrupt/unknown) → the explicitly-unsupported scenario so mock mode can
	// never silently render another pipeline's tracker. A missing value keeps the
	// historical V7 default (matches PortalApi's own parse default).
	const p = String(projectPipeline ?? '').trim().toLowerCase();
	if (p === '' || p === 'v7') return SINGLE_V7_PROJECT_FLOW_SCENARIO;
	// Core defaults to a representative sample-set fixture (the legacy
	// singleCoreProjectFlow fixture predates the sample-set tracker and renders empty).
	if (p === 'core') return 'coreMultiSampleSet';
	if (p === 'kits') return 'kitsPublishing';
	if (p === 'merged') return 'mergedReceived';
	return 'coreUnsupported';
}

/**
 * Snapshot-shaped mock for a scenario (`facts` + `iterationManifests` + `currentFlows`), cloned for safe mutation in tests.
 * Accepts any registered mock scenario key (flow keys included).
 *
 * @param {unknown} scenarioKey
 * @returns {{ facts: Record<string, unknown>[], iterationManifests: Record<string, unknown>[], currentFlows: Record<string, unknown> | null }}
 */
export function getMockPipelineStatusSnapshotForScenario(scenarioKey) {
	const key = requirePipelineStatusMockScenarioKey(scenarioKey);
	const facts = MOCK_FACT_ROWS_BY_SCENARIO[key];
	if (!facts) {
		throw new Error(`[pipelineStatus] Unknown pipeline status mock scenario: ${JSON.stringify(key)}`);
	}
	const manifests = MOCK_ITERATION_MANIFESTS_BY_SCENARIO[key] ?? [];
	const currentFlows = buildMockCurrentFlowsForScenario(key);
	const kind = MOCK_SCENARIO_REGISTRY[key].kind ?? 'unsupported';
	const graphPipeline =
		kind === 'core'
			? 'Core'
			: kind === 'discoveryPanels'
				? 'DiscoveryPanels'
				: kind === 'kits'
					? 'Kits'
					: kind === 'merged'
						? 'Merged'
						: 'V7';
	return {
		facts: cloneRows(facts).map(withMockDisplayNames),
		iterationManifests: cloneRows(manifests),
		currentFlows: currentFlows ? { ...currentFlows } : null,
		graph: mockPipelineStatusGraphForPipeline(graphPipeline),
	};
}

/** @param {unknown} scenarioKey @returns {Record<string, unknown>[]} */
export function getMockPipelineStatusFactRowsForScenario(scenarioKey) {
	return getMockPipelineStatusSnapshotForScenario(scenarioKey).facts;
}

/** @param {unknown} scenarioKey @returns {Record<string, unknown>[]} */
export function getMockPipelineStatusIterationManifestsForScenario(scenarioKey) {
	return getMockPipelineStatusSnapshotForScenario(scenarioKey).iterationManifests;
}
